import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import { UPDATE_REPLENISHMENT } from '@atom/graph/assetRequest';
import { Snackbar } from '@atom/mui';
import {
  Replenishment,
  ReplenishmentUpdateInput,
} from '@atom/types/assetRequest';
import api from '@atom/utilities/api';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';

import ViewRequestContext from '../ViewRequestContext';

import EditReplenishmentForm from './EditReplenishmentForm';

interface Props {
  replenishment: Replenishment;
  onCancel: () => void;
}

const EditReplenishmentView = ({ replenishment, onCancel }: Props) => {
  const { activeRequest, handleGetActiveRequest, onAssetRefresh } =
    useContext(ViewRequestContext);

  const [inputs, setInputs] = useState<Partial<ReplenishmentUpdateInput>>({
    quantityReceived: replenishment?.quantityReceived,
    unitCost: replenishment?.unitCost,
    totalCost: replenishment?.totalCost,
    invoiceNumber: replenishment?.invoiceNumber,
    comments: replenishment?.comments,
    dateReceived: replenishment?.dateReceived,
  });
  // TODO: [AM-16707]: Temp code until BE solution is built
  const [totalRemaining, setTotalRemaining] = useState<number>(0);
  const [totalRemainingLoading, setTotalRemainingLoading] =
    useState<boolean>(false);

  const getTotalRemaining = async () => {
    setTotalRemainingLoading(true);
    const { data } = await api.get(
      `${INVENTORY_ASSETS_ENDPOINT}/${activeRequest?.assetId}/usageSummary`,
    );

    setTotalRemaining(R.pathOr(0, ['totalRemaining'], data));
    setTotalRemainingLoading(false);
  };

  useEffect(() => {
    if (activeRequest?.assetId) {
      getTotalRemaining();
    }
  }, [activeRequest?.assetId]);

  const [updateReplenishment] = useMutation(UPDATE_REPLENISHMENT);

  const updateInput = (
    value: any,
    property: keyof ReplenishmentUpdateInput,
  ) => {
    setInputs(prev => ({ ...prev, [property]: value }));
  };

  const handleReset = () => {
    setInputs(null);
    onCancel();
  };

  const onEdit = async () => {
    try {
      await updateReplenishment({
        variables: {
          input: {
            requestId: activeRequest.id,
            replenishmentId: replenishment.id,
            ...inputs,
          },
        },
      });

      handleGetActiveRequest();
      handleReset();
      onAssetRefresh?.();
    } catch (error) {
      Snackbar.error({
        message: `An error occurred. Please Try Again.`,
      });
    }
  };

  const totalQuantityReceived = activeRequest?.replenishments?.reduce(
    (acc, replenishmentItem) => {
      return replenishmentItem.id === replenishment.id
        ? acc
        : acc + Number(replenishmentItem.quantityReceived || 0);
    },
    0,
  );

  const totalRemainingNegativeError = useMemo(() => {
    const totalRemainingDifference =
      totalRemaining -
      replenishment?.quantityReceived +
      inputs?.quantityReceived;

    return totalRemainingDifference < 0;
  }, [
    totalRemaining,
    inputs?.quantityReceived,
    replenishment?.quantityReceived,
  ]);

  return (
    <EditReplenishmentForm
      assetUnit={activeRequest?.assetUnit}
      totalOrdered={activeRequest?.quantityOrdered}
      totalReceived={totalQuantityReceived}
      replenishmentInputs={inputs}
      updateReplenishmentInput={updateInput}
      onEdit={onEdit}
      onCancel={handleReset}
      totalRemainingNegativeError={totalRemainingNegativeError}
      loading={totalRemainingLoading}
    />
  );
};

export default EditReplenishmentView;
